import { MarkerType } from "@xyflow/react";
import axios from "axios";
import { type Dispatch, type SetStateAction,useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import type { Tag } from "@/components/tags/types";
import apiRoutes from "@/constants/api-routes";
import useAuthHeader from "@/hooks/useAuthHeader";
import useToastAlert from "@/hooks/useToastAlert";
import { selectOrganizationId } from "@/redux/userSlice";

import { RULE_CANVAS_GRID_SIZE } from "../canvas/constants";
import { type Option, UI_ONLY_NODE_TYPES } from "../constants";
import { DEFAULT_EDGE_COLOR } from "../edges/BaseEdge";
import type { CreateRuleFormState, PlatformSelectOption, ReactionTypeOption, RuleFieldOption, RuleGroupOption, ScreenLocationOption } from "../modals/CreateRuleModal";
import { AFTER_LAST_SENTINEL_POSITION, collectAllRuleNodesInTree, CONTINUE_SENTINEL_POSITION,FOREACH_SENTINEL_POSITION } from "../nodes/loop/loopTypes";
import { buildGhostNode, buildInitialReactionNode, DEFAULT_NODE_OFFSET_Y } from "../nodes/nodeFactory";
import type { OperatorOption } from "../types/configs";
import { reconnectableForEdgeType, type RuleEdge, type RuleEdgeType } from "../types/edges";
import type { FilterNodeData, LoopNodeData, QueryNodeData, RuleNode, SortNodeData } from "../types/nodes";
import type { CategorizedOperators, UseRuleLifecycleOptions } from "../types/rule-lifecycle";
import { mapApiNodeToRuleNode, mapApiVariablesToState } from "../utils/api-mapping-utils";
import { deriveAdvancedTriggerOptions } from "../utils/advanced-trigger-utils";
import { deriveCollectionOptions } from "../utils/collection-utils";
import { fetchCollections } from "@/components/settings/collections/collectionsApi";
import {
	mapExpressionFunctionToOption,
	mapFormulaOperatorToOption,
	mapGlobalConstantToOption,
} from "../utils/expression-metadata-utils";
import {
	serializeLoopBodyForSave,
	stripTopLevelLoopNodeIds,
	validateSerializableLoopScopes,
} from "../utils/loop-persistable-sync";
import { sanitizeRuleGraphForSave } from "../utils/rule-graph-cleanup";
import { normalizeStopTagsInNodes, serializeStopNodeDataForSave } from "../utils/stop-tag-utils";
import {
	edgesToTransitions,
	transitionsToEdges,
} from "../utils/rule-transitions-utils";
import type { RuleVariable, RuleVariablesState } from "./useRuleVariables";

export const EDGE_MARKER_END = {
	type: MarkerType.Arrow,
	color: DEFAULT_EDGE_COLOR,
	height: 28,
	width: 28,
} as const;

/** Outgoing branch placeholders rebuilt on load (main canvas + each loop's sub-canvas). */
const BRANCH_GHOST_CONFIGS: {
	nodeType: string;
	branches: { handle: string; edgeType: RuleEdgeType; xDirection: -1 | 1 }[];
}[] = [
	{
		nodeType: "decision",
		branches: [
			{ handle: "true", edgeType: "decision-true", xDirection: -1 },
			{ handle: "false", edgeType: "decision-false", xDirection: 1 },
		],
	},
	{
		nodeType: "loop",
		branches: [{ handle: "loop-end", edgeType: "loop-end", xDirection: 1 }],
	},
];

/**
 * UI-only ghost nodes are stripped on save. On load, decision/loop branches inside
 * a loop's sub-canvas must be restored the same way as top-level `transitionEdges`.
 */
const ensureBranchGhostsInSingleLoopData = (loopData: LoopNodeData) => {
	for (const inner of loopData.loopNodes ?? []) {
		if (inner.type === "loop") {
			ensureBranchGhostsInSingleLoopData(inner.data as LoopNodeData);
		}
	}

	const loopNodes = loopData.loopNodes ?? [];
	const loopEdges = loopData.loopEdges ?? [];
	const newGhosts: RuleNode[] = [];
	const newEdges: RuleEdge[] = [];
	const NODE_CENTER_OFFSET_X = 125;
	const ghostOffsetX = RULE_CANVAS_GRID_SIZE * 8;
	const ghostOffsetY = DEFAULT_NODE_OFFSET_Y + NODE_CENTER_OFFSET_X;

	for (const node of loopNodes) {
		for (const { nodeType, branches } of BRANCH_GHOST_CONFIGS) {
			if (node.type !== nodeType) continue;
			const nodeCenterX = node.position.x + NODE_CENTER_OFFSET_X;

			for (const branch of branches) {
				const presentInLoop = [...loopEdges, ...newEdges].some(
					(e) => e.source === node.id && e.sourceHandle === branch.handle,
				);
				if (presentInLoop) continue;

				const ghost = buildGhostNode({
					x: nodeCenterX + ghostOffsetX * branch.xDirection,
					y: node.position.y + ghostOffsetY,
				});
				newGhosts.push(ghost);
				const reconnectable = reconnectableForEdgeType(branch.edgeType);
				newEdges.push({
					id: `edge__${node.id}-${branch.handle}-${ghost.id}`,
					source: node.id,
					sourceHandle: branch.handle,
					target: ghost.id,
					targetHandle: null,
					type: branch.edgeType,
					markerEnd: EDGE_MARKER_END,
					...(reconnectable ? { reconnectable } : {}),
				} as RuleEdge);
			}
		}
	}

	if (newGhosts.length > 0) {
		loopData.loopNodes = [...loopNodes, ...newGhosts];
		loopData.loopEdges = [...loopEdges, ...newEdges];
	}
};

/**
 * Derive database variables from query builder and filter nodes so the Toolbox
 * shows Single Row / Multi Row entries even when the API response
 * does not include them in a variables payload.
 */
const deriveDatabaseVariablesFromNodes = (nodes: RuleNode[]): Pick<RuleVariablesState, "databaseSingleRow" | "databaseMultiRow"> => {
	const databaseSingleRow: RuleVariable[] = [];
	const databaseMultiRow: RuleVariable[] = [];

	for (const node of nodes) {
		if (node.data.type === "query_builder") {
			const qd = node.data as QueryNodeData;
			if (!qd.name) continue;

			const raw = qd.recordChoice as string | undefined;
			const isAll = raw === "all" || raw === "allRecords";

			const variable: RuleVariable = {
				id: `${isAll ? "dbMulti" : "dbSingle"}-${node.id}`,
				name: qd.name,
				type: isAll ? "databaseMulti" : "databaseSingle",
				source: qd.databaseTableId != null && qd.databaseTableId !== "" ? String(qd.databaseTableId) : "",
				sourceNodeId: node.id,
				recordChoice: isAll ? "all" : "first",
			};

			if (isAll) {
				databaseMultiRow.push(variable);
			} else {
				databaseSingleRow.push(variable);
			}
		} else if (node.data.type === "filter") {
			const fd = node.data as FilterNodeData;
			if (!fd.name) continue;

			const raw = fd.recordScope as string | undefined;
			const isAll = raw === "all" || raw === "allRecords";

			const variable: RuleVariable = {
				id: `${isAll ? "dbMulti" : "dbSingle"}-${node.id}`,
				name: fd.name,
				type: isAll ? "databaseMulti" : "databaseSingle",
				source: fd.databaseTableId != null && fd.databaseTableId !== "" ? String(fd.databaseTableId) : "",
				sourceNodeId: node.id,
				recordChoice: isAll ? "all" : "first",
			};

			if (isAll) {
				databaseMultiRow.push(variable);
			} else {
				databaseSingleRow.push(variable);
			}
		} else if (node.data.type === "sort") {
			const sd = node.data as SortNodeData;
			if (!sd.name) continue;

			databaseMultiRow.push({
				id: `dbMulti-${node.id}`,
				name: sd.name,
				type: "databaseMulti",
				source: sd.databaseTableId != null && sd.databaseTableId !== "" ? String(sd.databaseTableId) : "",
				sourceNodeId: node.id,
				recordChoice: "all",
			});
		}
	}

	return { databaseSingleRow, databaseMultiRow };
};

const deriveReactionVariablesFromNodes = (nodes: RuleNode[]): Pick<RuleVariablesState, "reaction"> => {
	const reaction: RuleVariable[] = [];

	for (const node of nodes) {
		if (node.data.type !== "reaction") continue;
		if (!node.data.name) continue;

		reaction.push({
			id: `reaction-${node.id}`,
			name: node.data.name,
			type: "reaction",
			sourceNodeId: node.id,
		});
	}

	return { reaction };
};

/**
 * Recursively collects the IDs of every node that lives inside a loop's
 * sub-canvas (at any nesting depth). Used on load to filter inner-loop
 * transitions out of the main-canvas edge list — those edges are already
 * encoded in each loop node's `data.loopEdges`.
 */
const collectInnerLoopNodeIds = (nodes: RuleNode[]): Set<string> => {
	const ids = new Set<string>();
	for (const node of nodes) {
		if (node.type === "loop") {
			const loopData = node.data as LoopNodeData;
			for (const inner of loopData.loopNodes ?? []) {
				ids.add(inner.id);
				collectInnerLoopNodeIds([inner]).forEach((id) => ids.add(id));
			}
		}
	}
	return ids;
};

/** `transitionsToEdges` sets `loop-end` / `loop-end` for `when: endLoop`; main canvas LoopNode only has handle `out`. */
const normalizeMainCanvasLoopExitEdge = (e: RuleEdge, mappedNodes: RuleNode[]): RuleEdge => {
	const src = mappedNodes.find((n) => n.id === e.source);
	if (src?.type !== "loop") return e;
	if (e.type !== "loop-end" || e.sourceHandle !== "loop-end") return e;
	return {
		...e,
		id: `edge__${e.source}-out-${e.target}`,
		type: "default",
		sourceHandle: "out",
		reconnectable: undefined,
	};
};

/**
 * Node IDs reachable from `startIds` by following `from → to` on the flat
 * `rule.transitions` graph. The API often nests not only the direct `afterLast`
 * target under `loop.loopNodes`, but every node that continues the flow after
 * the loop (Decision → Assignment → …). Those must be peeled out using the
 * same graph the backend used to bundle them.
 */
const collectForwardReachableFromTransitions = (
	startIds: Iterable<string>,
	transitions: { from?: unknown; to?: unknown }[] | undefined,
): Set<string> => {
	const graph = new Map<string, string[]>();
	for (const t of transitions ?? []) {
		const from = String(t.from ?? "");
		const to = String(t.to ?? "");
		if (!from || !to) continue;
		if (!graph.has(from)) graph.set(from, []);
		graph.get(from)!.push(to);
	}

	const reachable = new Set<string>();
	const queue: string[] = [];
	for (const id of startIds) {
		const s = String(id);
		if (!reachable.has(s)) {
			reachable.add(s);
			queue.push(s);
		}
	}
	while (queue.length > 0) {
		const cur = queue.pop()!;
		for (const next of graph.get(cur) ?? []) {
			if (!reachable.has(next)) {
				reachable.add(next);
				queue.push(next);
			}
		}
	}
	return reachable;
};

const useRuleLifecycle = ({
	ruleId: initialRuleId,
	nodes,
	edges,
	setNodes,
	setEdges,
	variables,
	resetVariables,
	onRuleMetadataSaveSuccess,
}: UseRuleLifecycleOptions) => {
	const authHeader = useAuthHeader();
	const organizationId = useSelector(selectOrganizationId);
	const { handleToastAlert } = useToastAlert();
	const loadedTagsForOrgRef = useRef<string | number | null>(null);
	const loadedStopTagsForOrgRef = useRef<string | number | null>(null);
	const hasInitializedRef = useRef(false);

	const [ruleId, setRuleId] = useState<string | number>(initialRuleId ?? "");
	const [platformOptions, setPlatformOptions] = useState<PlatformSelectOption[]>([]);
	const [platformId, setPlatformId] = useState<string | number>("");
	const [groupOptions, setGroupOptions] = useState<RuleGroupOption[]>([]);
	const [groupsLoading, setGroupsLoading] = useState(true);
	const [screenLocationOptions, setScreenLocationOptions] = useState<ScreenLocationOption[]>([]);
	const [screenLocationsLoading, setScreenLocationsLoading] = useState(false);
	const [comparativeOperators, setComparativeOperators] = useState<OperatorOption[]>([]);
	const [assignmentOperators, setAssignmentOperators] = useState<OperatorOption[]>([]);
	const [functionOperators, setFunctionOperators] = useState<OperatorOption[]>([]);
	const [formulaOperators, setFormulaOperators] = useState<OperatorOption[]>([]);
	const [globalConstants, setGlobalConstants] = useState<Option[]>([]);
	const [formulaOperatorOptions, setFormulaOperatorOptions] = useState<Option[]>([]);
	const [expressionFunctionOptions, setExpressionFunctionOptions] = useState<Option[]>([]);
	const [isFormMetadataLoading, setIsFormMetadataLoading] = useState(false);
	const [tagsInCurrentOrganization, setTagsInCurrentOrganization] = useState<Tag[]>([]);
	const [stopTagsInOrganization, setStopTagsInOrganization] = useState<Tag[]>([]);
	const [ruleName, setRuleName] = useState("");
	const [ruleDescription, setRuleDescription] = useState("");
	const [ruleTags, setRuleTags] = useState<string[]>([]);
	const [screenLocation, setScreenLocation] = useState<ScreenLocationOption | null>(null);
	const [reactionTypeOptions, setReactionTypeOptions] = useState<Option[]>([]);
	const [ruleFieldOptions, setRuleFieldOptions] = useState<Option[]>([]);
	const [advancedTriggerOptions, setAdvancedTriggerOptions] = useState<Option[]>([]);
	const [collectionOptions, setCollectionOptions] = useState<Option[]>([]);
	const [ruleActive, setRuleActive] = useState(false);
	const [ruleGroups, setRuleGroups] = useState<any[]>([]);
	const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
	const isEditing = initialRuleId != null && initialRuleId !== "";
	const [createRuleModalOpen, setCreateRuleModalOpen] = useState(!isEditing);
	const [isCreatingRule, setIsCreatingRule] = useState(false);
	const [createRuleError, setCreateRuleError] = useState<string | null>(null);
	const [isSavingRule, setIsSavingRule] = useState(false);
	const [isLoadingRule, setIsLoadingRule] = useState(isEditing);

	const deriveReactionTypeOptions = useCallback((reactionTypes?: ReactionTypeOption[]): Option[] => {
		if (!reactionTypes?.length) return [];
		return reactionTypes.map((rt) => ({
			label: rt.friendlyName,
			value: String(rt.id),
			reactionTypeApiName: rt.name,
		}));
	}, []);

	const deriveRuleFieldOptions = useCallback((ruleFields?: RuleFieldOption[]): Option[] => {
		if (!ruleFields?.length) return [];
		return ruleFields.map((rf) => ({
			label: rf.name ?? "",
			value: String(rf.id),
			dataType: rf.dataFieldType?.name,
		}));
	}, []);

	const buildSerializableNodesForSave = useCallback(() => {
		const { nodes: sanitizedNodes, edges: cleanEdges } = sanitizeRuleGraphForSave(nodes, edges);
		const cleanNodes = normalizeStopTagsInNodes(sanitizedNodes);
		const reactionLocationNode = cleanNodes.find((n) => n.data.type === "reaction_location");
		const firstNodeEdge = reactionLocationNode
			? cleanEdges.find((e) => e.source === reactionLocationNode.id)
			: undefined;
		const firstNodeId = firstNodeEdge?.target;

		const mapNodePayload = (n: RuleNode, isFirst: boolean): Record<string, unknown> => {
			let mapped: any = n;

			if (n.data.type === "decision" && Array.isArray((n.data as any).statements)) {
				mapped = {
					...n,
					data: {
						...n.data,
						statements: ((n.data as any).statements as any[]).map(({ databaseTableField, screenTableField, screenField, advancedTrigger, resourceCollection, resource, ...rest }: any) => rest),
					},
				};
			}
			else if (n.data.type === "filter") {
				const fd = n.data as any;
				mapped = {
					...n,
					data: {
						type: fd.type,
						name: fd.name,
						databaseTableId: fd.databaseTableId != null ? Number(fd.databaseTableId) : null,
						screenTableId: fd.screenTableId != null ? Number(fd.screenTableId) : null,
						databaseTableVariableId: fd.databaseTableVariableId != null ? Number(fd.databaseTableVariableId) : null,
						sortByDatabaseTableFieldId: fd.sortByDatabaseTableFieldId != null ? Number(fd.sortByDatabaseTableFieldId) : null,
						sortByScreenTableFieldId: fd.sortByScreenTableFieldId != null ? Number(fd.sortByScreenTableFieldId) : null,
						sortOrder: fd.sortOrder ?? null,
						rowSelection: fd.recordScope === "allRecords" || fd.recordScope === "all" ? "all" : "first",
						filterLogicExpression: fd.filterLogicExpression ?? null,
						statements: Array.isArray(fd.conditions)
							? fd.conditions.map(({ databaseTableFieldId, screenTableFieldId, operator, value }: any, i: number) => ({
								databaseTableFieldId: databaseTableFieldId != null ? Number(databaseTableFieldId) : null,
								screenTableFieldId: screenTableFieldId != null ? Number(screenTableFieldId) : null,
								operator,
								value,
								num: i + 1,
							}))
							: [],
					},
				};
			}
			else if (n.data.type === "sort") {
				const { conditions, tableName, ...restData } = n.data as any;
				mapped = {
					...n,
					data: {
						...restData,
						databaseTableName: restData.databaseTableId ? tableName : undefined,
						screenTableName: restData.screenTableId ? tableName : undefined,
						statements: Array.isArray(conditions)
							? conditions.map(({ sortByDatabaseTableFieldId, sortByScreenTableFieldId, sortOrder }: any, i: number) => ({
								sortByDatabaseTableFieldId, sortByScreenTableFieldId, sortOrder, orderNum: i + 1,
							}))
							: [],
					},
				};
			}
			else if (n.data.type === "loop") {
				const ld = n.data as LoopNodeData;
				const { loopNodes: _ln, loopEdges: _le, ...loopFields } = ld;
				mapped = {
					...n,
					data: {
						...loopFields,
						startSentinelPosition: ld.startSentinelPosition ?? FOREACH_SENTINEL_POSITION,
						endSentinelPosition: ld.endSentinelPosition ?? AFTER_LAST_SENTINEL_POSITION,
						continueSentinelPosition: ld.continueSentinelPosition ?? CONTINUE_SENTINEL_POSITION,
					},
				};
			}
			else if (n.data.type === "event_listener") {
				const ed = n.data as {
					type: string;
					name?: string;
					targetScreenFieldIds?: string[];
					targetScreenFieldNames?: string[];
					action?: string;
				};
				mapped = {
					...n,
					data: {
						type: ed.type,
						name: ed.name,
						targetScreenFieldIds: (ed.targetScreenFieldIds ?? [])
							.map((id) => Number(id))
							.filter((id) => !Number.isNaN(id)),
						action: ed.action ?? "click",
					},
				};
			}
			else if (n.data.type === "action") {
				const ad = n.data as {
					type: string;
					name?: string;
					actionType?: string;
				};
				mapped = {
					...n,
					data: {
						type: ad.type,
						name: ad.name,
						actionType: ad.actionType ?? "click",
					},
				};
			}
			else if (n.data.type === "stop") {
				mapped = {
					...n,
					data: serializeStopNodeDataForSave(n.data),
				};
			}

			const { loopNodeId: _scope, ...restMapped } = mapped as Record<string, unknown>;
			const data = { ...(mapped.data as Record<string, unknown>), isFirst };
			delete (data as Record<string, unknown>).loopNodeId;
			return { ...restMapped, data };
		};

		const serializableNodes = cleanNodes
			.filter((n) => !UI_ONLY_NODE_TYPES.has(n.type ?? ""))
			.map((n) => {
				const isFirst = n.id === firstNodeId;
				if (n.data.type !== "loop") {
					return mapNodePayload(n, isFirst);
				}

				const ld = n.data as LoopNodeData;
				const base = mapNodePayload(n, isFirst);
				const body = serializeLoopBodyForSave(
					n.id,
					ld.loopNodes ?? [],
					ld.loopEdges ?? [],
					(child, _parentLoopId) => mapNodePayload(child, false),
					cleanEdges,
				);
				return {
					...base,
					data: {
						...(base.data as Record<string, unknown>),
						loopNodes: body.loopNodes,
						loopEdges: body.loopEdges,
					},
				};
			});

		const saveTransitions = edgesToTransitions(cleanEdges, cleanNodes);
		const scopeErrors = validateSerializableLoopScopes(serializableNodes, saveTransitions);

		return {
			serializableNodes,
			saveTransitions,
			scopeErrors,
		};
	}, [nodes, edges]);

	const handleCreateRule = useCallback(async (formState: CreateRuleFormState) => {
		const trimmedName = formState.ruleName.trim();
		const trimmedDescription = formState.ruleDescription.trim();
		const rulePayload = {
			active: false,
			description: trimmedDescription,
			groups: formState.groups ?? [],
			isDraft: false, // TODO: think about this from a product perspective
			name: trimmedName,
			reactions: [],
			tags: formState.ruleTags ?? [],
			screenLocationId: formState.screenLocation?.id,
			platformId: formState.platformId,
		};

		setIsCreatingRule(true);

		try {
			const response = await axios.post(apiRoutes.createRule, rulePayload, { headers: authHeader });
			setRuleName(trimmedName);
			setRuleDescription(trimmedDescription);
			setRuleTags(formState.ruleTags ?? []);
			setScreenLocation(formState.screenLocation);
			setNodes([
				buildInitialReactionNode(
					trimmedName,
					formState.screenLocation?.name ?? "",
					formState.screenLocation?.previewUrl,
				),
			]);
			setPlatformId(formState.platformId);
			setEdges([]);
			setCreateRuleModalOpen(false);
			setRuleId(response.data.id);
			setRuleGroups(formState.groups ?? []);
			setLastSavedAt(new Date());
		} catch (error) {
			setCreateRuleError((error as any).response?.data?.message ?? "Failed to create rule. Please try again.");
			console.error("Failed to create rule", error);
		} finally {
			setIsCreatingRule(false);
		}
	}, [authHeader, setCreateRuleModalOpen, setEdges, setNodes, setPlatformId, setRuleName, setRuleGroups]);

	const handleRuleSave = useCallback(async (): Promise<boolean | undefined> => {
		if (!ruleId) {
			console.error("Cannot save rule: ruleId is not set");
			return false;
		}

		setIsSavingRule(true);
		try {
			const { serializableNodes, saveTransitions, scopeErrors } = buildSerializableNodesForSave();
			if (scopeErrors.length > 0) {
				handleToastAlert("error", scopeErrors[0]!.message);
				return false;
			}
			const rootNode = nodes.find((n) => n.data.type === "reaction_location");

			const rulePayload = {
				active: ruleActive,
				description: ruleDescription,
				groups: ruleGroups,
				isDraft: false,
				name: ruleName,
				nodes: serializableNodes,
				transitions: saveTransitions,
				variables: variables ? {
					simple: variables.simple,
					formula: variables.formula,
				} : undefined,
				tags: ruleTags,
				screenLocationId: screenLocation?.id ?? undefined,
				platformId: platformId,
				rootNodePosX: rootNode?.position.x,
				rootNodePosY: rootNode?.position.y,
			};

			await axios.put(apiRoutes.updateRule(ruleId), rulePayload, { headers: authHeader });
			setLastSavedAt(new Date());
			return true;
		} catch (error) {
			console.error("Failed to save rule", error);
			handleToastAlert("error", "Failed to save rule. Please try again.");
			return false;
		} finally {
			setIsSavingRule(false);
		}
	}, [ruleId, buildSerializableNodesForSave, edges, nodes, variables, ruleActive, ruleGroups, ruleDescription, ruleName, ruleTags, screenLocation, platformId, authHeader, handleToastAlert]);

	const handleSaveRuleMetadata = useCallback(async (formState: CreateRuleFormState) => {
		if (!ruleId) {
			console.error("Cannot save rule metadata: ruleId is not set");
			return;
		}

		setIsSavingRule(true);

		try {
			const trimmedName = formState.ruleName.trim();
			const trimmedDescription = formState.ruleDescription.trim();
			const { serializableNodes, saveTransitions, scopeErrors } = buildSerializableNodesForSave();
			if (scopeErrors.length > 0) {
				handleToastAlert("error", scopeErrors[0]!.message);
				return;
			}
			const rootNode = nodes.find((n) => n.data.type === "reaction_location");

			const rulePayload = {
				active: ruleActive,
				description: trimmedDescription,
				groups: formState.groups ?? [],
				isDraft: false,
				name: trimmedName,
				nodes: serializableNodes,
				transitions: saveTransitions,
				variables: variables ? {
					simple: variables.simple,
					formula: variables.formula,
				} : undefined,
				tags: formState.ruleTags ?? [],
				screenLocationId: formState.screenLocation?.id ?? undefined,
				platformId: formState.platformId,
				rootNodePosX: rootNode?.position.x,
				rootNodePosY: rootNode?.position.y,
			};

			await axios.put(apiRoutes.updateRule(ruleId), rulePayload, { headers: authHeader });

			setRuleName(trimmedName);
			setRuleDescription(trimmedDescription);
			setRuleTags(formState.ruleTags ?? []);
			setRuleGroups(formState.groups ?? []);
			setScreenLocation(formState.screenLocation);
			setPlatformId(formState.platformId);
			setNodes((prev) =>
				prev.map((n) =>
					n.type === "reaction_location"
						? {
							...n,
							data: {
								...n.data,
								name: trimmedName,
								screenLocation: formState.screenLocation?.name ?? "",
							},
						}
						: n,
				),
			);

			setLastSavedAt(new Date());
			setCreateRuleError(null);
			setCreateRuleModalOpen(false);
			onRuleMetadataSaveSuccess?.();
		} catch (error) {
			setCreateRuleError((error as any).response?.data?.message ?? "Failed to save rule details. Please try again.");
			console.error("Failed to save rule details", error);
		} finally {
			setIsSavingRule(false);
		}
	}, [
		ruleId,
		buildSerializableNodesForSave,
		edges,
		nodes,
		variables,
		ruleActive,
		authHeader,
		handleToastAlert,
		onRuleMetadataSaveSuccess,
		setNodes,
		setPlatformId,
		setRuleDescription,
		setRuleName,
		setRuleTags,
		setRuleGroups,
		setScreenLocation,
		setCreateRuleModalOpen,
	]);

	const handleRuleLoad = useCallback(async (ruleIdToLoad: string | number) => {
		setIsLoadingRule(true);
		try {
			const [response, operatorsByCategory] = await Promise.all([
				axios.get(apiRoutes.getRule(ruleIdToLoad), { headers: authHeader }),
				loadFormMetadata(),
			]);
			const rule = response.data;

			setRuleId(rule.id);
			setRuleName(rule.name ?? "");
			setRuleDescription(rule.description ?? "");
			setRuleActive(rule.active ?? false);
			setRuleGroups(rule.groups ?? []);
			setRuleTags(rule.tags?.map((t: Tag) => t.name) ?? []);
			setLastSavedAt(new Date(rule.updatedAt));

			setPlatformId(rule.platformId ?? "");

			const [locations, loadedAdvancedTriggers, loadedCollections] = await Promise.all([
				rule.platformId ? loadAvailableScreenLocations(rule.platformId) : Promise.resolve([]),
				rule.platformId && organizationId
					? loadAdvancedTriggers(rule.platformId)
					: Promise.resolve([] as Option[]),
				rule.platformId
					? loadCollections(rule.platformId)
					: Promise.resolve([] as Option[]),
			]);

			const screenLocationId = rule.screenLocationId ?? rule.screenLocation;
			const matchedLocation = locations.find((l) => String(l.id) === String(screenLocationId));
			const locationReactionTypes = matchedLocation?.reactionTypes;
			const locationRuleFields = matchedLocation?.ruleFields;

			if (screenLocationId) {
				const name = rule.configScreenLocation?.name
					?? rule.screenLocationName
					?? matchedLocation?.name
					?? "";
				const matchedPreview =
					matchedLocation?.previewUrl != null && String(matchedLocation.previewUrl).trim() !== ""
						? String(matchedLocation.previewUrl).trim().replace(/\r/g, "")
						: undefined;
				setScreenLocation({
					id: screenLocationId,
					name,
					...(matchedPreview != null ? { previewUrl: matchedPreview } : {}),
					reactionTypes: locationReactionTypes,
					ruleFields: locationRuleFields,
				});
			} else {
				setScreenLocation(null);
			}

			const apiSimpleVariables = rule.variables?.simple ?? [];
			const apiDatabaseTableVariables = rule.variables?.databaseTable ?? [];
			const apiReactionNodes = Array.isArray(rule.nodes) ? rule.nodes.filter((n: any) => n.data.type === "reaction") : [];
			const mappedNodes: RuleNode[] = Array.isArray(rule.nodes)
				? rule.nodes.map((n: any) =>
					mapApiNodeToRuleNode(n, operatorsByCategory, apiSimpleVariables, apiReactionNodes, apiDatabaseTableVariables),
				)
				: [];

			if (
				locationReactionTypes?.length ||
				locationRuleFields?.length ||
				loadedAdvancedTriggers.length ||
				loadedCollections.length
			) {
				for (const node of mappedNodes) {
					if (node.data.type === "reaction") {
						if (node.data.reactionTypeId && locationReactionTypes?.length) {
							const rt = locationReactionTypes.find((r) => String(r.id) === node.data.reactionTypeId);
							(node.data as any).reactionTypeName = rt?.name ?? "";
							(node.data as any).reactionTypeFriendlyName = rt?.friendlyName ?? "";
						}
						if (node.data.screenFieldId && locationRuleFields?.length) {
							const rf = locationRuleFields.find((f) => String(f.id) === node.data.screenFieldId);
							(node.data as any).screenFieldName =  rf?.name ?? "";
						}
					}
					if (node.data.type === "decision" && Array.isArray(node.data.statements)) {
						for (const stmt of node.data.statements as any[]) {
							if (stmt.advancedTriggerId && !stmt.advancedTriggerName) {
								const match = loadedAdvancedTriggers.find((t) => t.value === String(stmt.advancedTriggerId));
								stmt.advancedTriggerName = match?.label ?? stmt.advancedTrigger?.name ?? "";
							}
							if (stmt.valueAdvancedTriggerId && !stmt.valueAdvancedTriggerName) {
								const match = loadedAdvancedTriggers.find((t) => t.value === String(stmt.valueAdvancedTriggerId));
								stmt.valueAdvancedTriggerName = match?.label ?? stmt.valueAdvancedTrigger?.name ?? "";
							}
							if (stmt.resourceCollectionId && !stmt.collectionName) {
								const match = loadedCollections.find((c) => c.value === String(stmt.resourceCollectionId));
								stmt.collectionName = match?.label ?? stmt.resourceCollection?.name ?? "";
							}
							if (stmt.valueResourceCollectionId && !stmt.valueCollectionName) {
								const match = loadedCollections.find((c) => c.value === String(stmt.valueResourceCollectionId));
								stmt.valueCollectionName = match?.label ?? stmt.valueResourceCollection?.name ?? "";
							}
						}
					}
				}
			}

			const eventListenerNodes = mappedNodes.filter((n) => n.data.type === "event_listener");
			if (eventListenerNodes.length > 0 && screenLocationId) {
				try {
					const fieldsRes = await axios.get(apiRoutes.getScreenFields(screenLocationId), {
						headers: authHeader,
					});
					const buttonFields = (fieldsRes.data ?? []).filter(
						(f: { htmlFieldType?: { name?: string } }) =>
							String(f.htmlFieldType?.name ?? "").toLowerCase() === "button",
					);
					for (const node of eventListenerNodes) {
						const ids = node.data.targetScreenFieldIds ?? [];
						if (!ids || !Array.isArray(ids) || ids.length === 0) continue;
						(node.data as any).targetScreenFieldNames = ids.map((id) => {
							const match = buttonFields.find((f: { id: number }) => String(f.id) === String(id));
							return match?.name ?? "";
						});
					}
				} catch (error) {
					console.error("Failed to enrich event listener field names", error);
				}
			}

			const screenLocationName = rule.configScreenLocation?.name ?? "";
			const reactionLocationNode = buildInitialReactionNode(
				rule.name ?? "",
				screenLocationName,
				matchedLocation?.previewUrl,
			);

			if (rule.rootNodePosX != null && rule.rootNodePosY != null) {
				reactionLocationNode.position = { x: Number(rule.rootNodePosX), y: Number(rule.rootNodePosY) };
			}

			const allTransitionEdges = Array.isArray(rule.transitions)
				? transitionsToEdges(rule.transitions)
				: [];

			// The backend treats the loop's `afterLast` branch as loop-owned and may
			// duplicate the entire downstream chain inside `data.loopNodes` — not only
			// the first node off `loop-end`. Peel off every ID reachable from
			// this loop's `afterLast` targets by walking the flat transitions graph.
			const flatTransitions = Array.isArray(rule.transitions) ? rule.transitions : [];
			const existingMappedIds = new Set(mappedNodes.map((n) => n.id));
			const rescuedNodes: RuleNode[] = [];

			for (const node of mappedNodes) {
				if (node.type !== "loop") continue;
				const ld = node.data as LoopNodeData;
				if (!Array.isArray(ld.loopNodes) || ld.loopNodes.length === 0) continue;

				const loopId = String(node.id);
				const afterLastStarts = flatTransitions
					.filter((t: any) => String(t.from) === loopId && t.when === "endLoop")
					.map((t: any) => String(t.to));
				if (afterLastStarts.length === 0) continue;

				const exitReachable = collectForwardReachableFromTransitions(afterLastStarts, flatTransitions);

				for (const innerNode of ld.loopNodes) {
					if (exitReachable.has(innerNode.id) && !existingMappedIds.has(innerNode.id)) {
						rescuedNodes.push(innerNode);
						existingMappedIds.add(innerNode.id);
					}
				}
				ld.loopNodes = ld.loopNodes.filter((n) => !exitReachable.has(n.id));
			}

			if (rescuedNodes.length > 0) {
				mappedNodes.push(...rescuedNodes);
			}

			// Explicit `loopNodeId` on every body node/edge is the single source of truth;
			// top-level nodes must not carry loop scope.
			const scopedNodes = stripTopLevelLoopNodeIds(mappedNodes);
			mappedNodes.length = 0;
			mappedNodes.push(...scopedNodes);

			// Inner loop nodes are stored in each loop node's data.loopNodes and
			// their transitions are also included in the flat transitions list (for
			// the backend). Filter them out here so they don't appear on the main
			// canvas — the sub-canvas re-hydrates from data.loopNodes/loopEdges.
			const innerNodeIds = collectInnerLoopNodeIds(mappedNodes);
			const transitionEdges = (
				innerNodeIds.size === 0
					? allTransitionEdges
					: allTransitionEdges.filter((e) => !innerNodeIds.has(e.source) && !innerNodeIds.has(e.target))
			).map((e) => normalizeMainCanvasLoopExitEdge(e, mappedNodes));

			/**
		 * Constructs an edge from the Reaction Location to the first real node
		 */
			const firstNode = mappedNodes.find((n: any) => n.data?.isFirst) ?? mappedNodes[0];
			if (firstNode) {
				transitionEdges.unshift({
					id: `edge__${reactionLocationNode.id}-out-${firstNode.id}`,
					markerEnd: EDGE_MARKER_END,
					source: reactionLocationNode.id,
					sourceHandle: "out",
					target: firstNode.id,
					targetHandle: "in",
					type: "default",
				} as RuleEdge);
			}

			for (const n of mappedNodes) {
				if (n.type === "loop") {
					ensureBranchGhostsInSingleLoopData(n.data as LoopNodeData);
				}
			}

			/**
		 * Ghost nodes are UI-only and stripped during save. Rebuild them for
		 * any Decision or Loop node that is missing its expected outgoing
		 * branches so the canvas looks the same as when the node was created.
		 *
		 * Note: the loop-start branch is internal to the sub-canvas and is
		 * never present in main-canvas transitions, so only loop-end
		 * needs a ghost placeholder on the outer graph.
		 */
			const ghostNodes: RuleNode[] = [];
			const ghostEdges: RuleEdge[] = [];
			const NODE_CENTER_OFFSET_X = 125;
			const ghostOffsetX = RULE_CANVAS_GRID_SIZE * 8;
			const ghostOffsetY = DEFAULT_NODE_OFFSET_Y + NODE_CENTER_OFFSET_X;

			const branchConfigs: { nodeType: string; branches: { handle: string; edgeType: RuleEdgeType; xDirection: -1 | 1 }[] }[] = [
				{
					nodeType: "decision",
					branches: [
						{ handle: "true", edgeType: "decision-true", xDirection: -1 },
						{ handle: "false", edgeType: "decision-false", xDirection: 1 },
					],
				},
				{
					nodeType: "loop",
					branches: [
						{ handle: "out", edgeType: "default", xDirection: 1 },
					],
				},
			];

			for (const { nodeType, branches } of branchConfigs) {
				const branchingNodes = mappedNodes.filter((n) => n.type === nodeType);

				for (const node of branchingNodes) {
					const nodeCenterX = node.position.x + NODE_CENTER_OFFSET_X;

					for (const branch of branches) {
						const hasEdge = transitionEdges.some((e) => {
							if (e.source !== node.id) return false;
							if (nodeType === "loop" && branch.handle === "out") {
								return e.sourceHandle === "loop-end" || e.sourceHandle === "out";
							}
							return e.sourceHandle === branch.handle;
						});
						if (hasEdge) continue;

						const ghost = buildGhostNode({
							x: nodeCenterX + ghostOffsetX * branch.xDirection,
							y: node.position.y + ghostOffsetY,
						});
						ghostNodes.push(ghost);
						const reconnectable = reconnectableForEdgeType(branch.edgeType);
						ghostEdges.push({
							id: `edge__${node.id}-${branch.handle}-${ghost.id}`,
							source: node.id,
							sourceHandle: branch.handle,
							target: ghost.id,
							targetHandle: null,
							type: branch.edgeType,
							markerEnd: EDGE_MARKER_END,
							...(reconnectable ? { reconnectable } : {}),
						} as RuleEdge);
					}
				}
			}

			setNodes([reactionLocationNode, ...mappedNodes, ...ghostNodes]);
			setEdges([...transitionEdges, ...ghostEdges]);

			if (resetVariables) {
				const apiVars = rule.variables ? mapApiVariablesToState(rule.variables) : {};
				const nodesForDerivation = collectAllRuleNodesInTree(mappedNodes);
				const derivedDb = deriveDatabaseVariablesFromNodes(nodesForDerivation);
				const derivedReaction = deriveReactionVariablesFromNodes(nodesForDerivation);
				resetVariables({
					...apiVars,
					databaseSingleRow: derivedDb.databaseSingleRow,
					databaseMultiRow: derivedDb.databaseMultiRow,
					reaction: derivedReaction.reaction,
				});
			}

			setCreateRuleModalOpen(false);
		} catch (error) {
			console.error("Failed to load rule", error);
			handleToastAlert("error", "Failed to load rule. Please try again.");
		} finally {
			setIsLoadingRule(false);
		}
	}, [authHeader, handleToastAlert, setNodes, setEdges, resetVariables]);

	const loadAdvancedTriggers = async (selectedPlatformId: string | number): Promise<Option[]> => {
		if (!selectedPlatformId || !organizationId) {
			setAdvancedTriggerOptions([]);
			return [];
		}

		try {
			const response = await axios.get(
				apiRoutes.getAdvancedTriggers(organizationId, selectedPlatformId),
				{ headers: authHeader },
			);
			const options = deriveAdvancedTriggerOptions(
				Array.isArray(response.data) ? response.data : [],
			);
			setAdvancedTriggerOptions(options);
			return options;
		} catch (error) {
			console.error("Failed to load advanced triggers", error);
			setAdvancedTriggerOptions([]);
			return [];
		}
	};

	const loadCollections = async (selectedPlatformId: string | number): Promise<Option[]> => {
		if (!selectedPlatformId) {
			setCollectionOptions([]);
			return [];
		}

		try {
			const data = await fetchCollections(authHeader, selectedPlatformId);
			const options = deriveCollectionOptions(Array.isArray(data) ? data : []);
			setCollectionOptions(options);
			return options;
		} catch (error) {
			console.error("Failed to load collections", error);
			setCollectionOptions([]);
			return [];
		}
	};

	const loadAvailableScreenLocations = async (selectedPlatformId: string | number): Promise<ScreenLocationOption[]> => {
		if (!selectedPlatformId) {
			setScreenLocationOptions([]);
			setScreenLocationsLoading(false);
			return [];
		}

		setScreenLocationsLoading(true);
		setScreenLocationOptions([]);
		try {
			const response = await axios.get(apiRoutes.getRuleLocationsForWizard(selectedPlatformId), { headers: authHeader });
			const mappedLocations: ScreenLocationOption[] = Array.isArray(response.data)
				? response.data
					.map((location: any) => {
						const screenLocation = location;
						if (!screenLocation?.id || !screenLocation?.name) {
							return null;
						}
						const rawPreview =
						typeof screenLocation.previewUrl === "string" ? screenLocation.previewUrl : "";
						const previewUrl = rawPreview.trim().replace(/\r/g, "") || undefined;

						return {
							id: screenLocation.id,
							name: screenLocation.name,
							...(previewUrl != null ? { previewUrl } : {}),
							reactionTypes: Array.isArray(screenLocation.reactiontypes)
								? screenLocation.reactiontypes
								: undefined,
							ruleFields: Array.isArray(screenLocation.rulefields)
								? screenLocation.rulefields
								: undefined,
						};
					})
					.filter(Boolean) as ScreenLocationOption[]
				: [];
			setScreenLocationOptions(mappedLocations);
			return mappedLocations;
		} catch (error) {
			console.error("Failed to load screen locations", error);
			setScreenLocationOptions([]);
			return [];
		} finally {
			setScreenLocationsLoading(false);
		}
	};

	const loadAvailablePlatforms = async () => {
		if (!organizationId) {
			return;
		}

		try {
			const response = await axios.get(apiRoutes.getPlatforms(organizationId), { headers: authHeader });
			const mappedPlatforms: PlatformSelectOption[] = Array.isArray(response.data)
				? response.data
					.map((platformOrg: any) => {
						const platform = platformOrg?.platform ?? platformOrg;
						if (!platform?.id || !platform?.name) {
							return null;
						}

						return { id: platform.id, name: platform.name };
					})
					.filter(Boolean) as PlatformSelectOption[]
				: [];

			setPlatformOptions(mappedPlatforms);
		} catch (error) {
			console.error("Failed to load platforms", error);
			setPlatformOptions([]);
		}
	};

	const loadAvailableTags = async () => {
		if (!organizationId) {
			return;
		}

		if (loadedTagsForOrgRef.current === organizationId) {
			return;
		}

		try {
			const response = await axios.get(apiRoutes.getTags, { headers: authHeader });
			setTagsInCurrentOrganization(response.data);
			loadedTagsForOrgRef.current = organizationId;
		} catch (error) {
			console.error("Failed to load tags", error);
			setTagsInCurrentOrganization([]);
		}
	};

	const loadStopTags = async () => {
		if (!organizationId) {
			return;
		}

		if (loadedStopTagsForOrgRef.current === organizationId) {
			return;
		}

		try {
			const response = await axios.get(apiRoutes.getStopTags, { headers: authHeader });
			setStopTagsInOrganization(response.data);
			loadedStopTagsForOrgRef.current = organizationId;
		} catch (error) {
			console.error("Failed to load stop tags", error);
			setStopTagsInOrganization([]);
		}
	};

	const normalizeOperatorOption = (raw: unknown): OperatorOption | null => {
		if (!raw || typeof raw !== "object") return null;
		const r = raw as Record<string, unknown>;
		const operator = typeof r.operator === "string" ? r.operator.trim() : "";
		if (!operator) return null;
		const id = r.id != null ? String(r.id) : operator;
		return { id, operator };
	};

	const loadAvailableGroups = async () => {
		setGroupsLoading(true);
		try {
			const response = await axios.get(apiRoutes.getGroups, { headers: authHeader });
			const mapped: RuleGroupOption[] = Array.isArray(response.data)
				? response.data
					.map((g: { id?: unknown; name?: unknown } | null) => {
						if (g == null || g.id == null || g.name == null) return null;
						return { id: String(g.id), name: String(g.name) };
					})
					.filter((item): item is RuleGroupOption => item != null)
				: [];
			setGroupOptions(mapped);
		} catch (error) {
			console.error("Failed to load groups", error);
			setGroupOptions([]);
		} finally {
			setGroupsLoading(false);
		}
	};

	const loadFormMetadata = async (): Promise<CategorizedOperators> => {
		const empty: CategorizedOperators = { comparative: [], assignment: [] };
		if (!organizationId) return empty;
		setIsFormMetadataLoading(true);
		try {
			const response = await axios.get(apiRoutes.getFormMetadata, { headers: authHeader });
			const payload = response.data ?? {};
			const comparativeRaw = payload.comparativeOperators ?? [];
			const assignmentRaw = payload.assignmentOperators ?? [];
			const comparative = (Array.isArray(comparativeRaw) ? comparativeRaw : [])
				.map(normalizeOperatorOption)
				.filter((o): o is OperatorOption => o != null);
			const assignment = (Array.isArray(assignmentRaw) ? assignmentRaw : [])
				.map(normalizeOperatorOption)
				.filter((o): o is OperatorOption => o != null);
			const functionOpsRaw = payload.functionOperators ?? [];
			const functionOperatorsNorm = (Array.isArray(functionOpsRaw) ? functionOpsRaw : [])
				.map(normalizeOperatorOption)
				.filter((o): o is OperatorOption => o != null);
			const formulaOpsRaw = payload.formulaOperators ?? [];
			const formulaOperatorsNorm = (Array.isArray(formulaOpsRaw) ? formulaOpsRaw : [])
				.map(normalizeOperatorOption)
				.filter((o): o is OperatorOption => o != null);
			const formulaOptsForUi = (Array.isArray(formulaOpsRaw) ? formulaOpsRaw : []).map((op) =>
				mapFormulaOperatorToOption(op as OperatorOption & { label?: string }),
			);
			const funcsRaw = payload.functions ?? [];
			const expressionFuncs = (Array.isArray(funcsRaw) ? funcsRaw : [])
				.map(mapExpressionFunctionToOption)
				.filter((o): o is Option => o != null);
			const globalRaw = payload.globalConstants ?? [];
			const globalOpts = (Array.isArray(globalRaw) ? globalRaw : [])
				.map(mapGlobalConstantToOption)
				.filter((o): o is Option => o != null);

			setComparativeOperators(comparative);
			setAssignmentOperators(assignment);
			setFunctionOperators(functionOperatorsNorm);
			setFormulaOperators(formulaOperatorsNorm);
			setFormulaOperatorOptions(formulaOptsForUi);
			setExpressionFunctionOptions(expressionFuncs);
			setGlobalConstants(globalOpts);
			return { comparative, assignment };
		} catch (error) {
			console.error("Failed to load form metadata", error);
			setComparativeOperators([]);
			setAssignmentOperators([]);
			setFunctionOperators([]);
			setFormulaOperators([]);
			setFormulaOperatorOptions([]);
			setExpressionFunctionOptions([]);
			setGlobalConstants([]);
			return empty;
		} finally {
			setIsFormMetadataLoading(false);
		}
	};

	useEffect(() => {
		if (!organizationId) {
			setGroupsLoading(false);
			return;
		}
		// Wait until auth is ready — authHeader is {} when the Descope token
		// hasn't resolved yet. Without this guard the API call fires with no
		// Authorization header and returns 401 before the token arrives.
		if (!authHeader.Authorization) return;
		if (hasInitializedRef.current) return;
		hasInitializedRef.current = true;

		loadAvailablePlatforms();
		loadAvailableTags();
		loadStopTags();
		loadAvailableGroups();

		if (initialRuleId != null && initialRuleId !== "") {
			handleRuleLoad(initialRuleId);
		} else {
			void loadFormMetadata();
		}
	}, [organizationId, handleRuleLoad, authHeader]);

	useEffect(() => {
		if (platformId) {
			loadAvailableScreenLocations(platformId);
			void loadAdvancedTriggers(platformId);
			void loadCollections(platformId);
		} else {
			setScreenLocationOptions([]);
			setScreenLocationsLoading(false);
			setAdvancedTriggerOptions([]);
			setCollectionOptions([]);
		}
	}, [platformId]);

	useEffect(() => {
		setReactionTypeOptions(deriveReactionTypeOptions(screenLocation?.reactionTypes));
		setRuleFieldOptions(deriveRuleFieldOptions(screenLocation?.ruleFields));
	}, [screenLocation, deriveReactionTypeOptions, deriveRuleFieldOptions]);

	/** Fields persisted with the rule besides canvas flow — used for dirty-detection side effects in RuleBuilder (with flow + variable snapshots). */
	const ruleMetadataForDirtyCheck = useMemo(
		() => ({
			name: ruleName,
			description: ruleDescription,
			tags: [...ruleTags].sort(),
			screenLocationId: screenLocation?.id ?? null,
			platformId,
			active: ruleActive,
			groups: ruleGroups,
			simple: variables?.simple ?? [],
			formula: variables?.formula ?? [],
			databaseSingleRow: variables?.databaseSingleRow ?? [],
			databaseMultiRow: variables?.databaseMultiRow ?? [],
		}),
		[
			ruleName,
			ruleDescription,
			ruleTags,
			screenLocation,
			platformId,
			ruleActive,
			ruleGroups,
			variables?.simple,
			variables?.formula,
			variables?.databaseSingleRow,
			variables?.databaseMultiRow,
		],
	);

	return {
		createRuleModalOpen,
		setCreateRuleModalOpen,
		ruleId,
		ruleName,
		setRuleName,
		ruleDescription,
		ruleTags,
		screenLocation,
		ruleGroups,
		platformId,
		setPlatformId,
		platformOptions,
		groupOptions,
		groupsLoading,
		screenLocationOptions,
		screenLocationsLoading,
		tagsInCurrentOrganization,
		stopTagsInOrganization,
		lastSavedAt,
		isCreatingRule,
		createRuleError,
		setCreateRuleError,
		isLoadingRule,
		ruleMetadataForDirtyCheck,
		isSavingRule,
		handleCreateRule,
		handleSaveRuleMetadata,
		handleRuleSave,
		handleRuleLoad,
		comparativeOperators,
		assignmentOperators,
		functionOperators,
		formulaOperators,
		globalConstants,
		formulaOperatorOptions,
		expressionFunctionOptions,
		isFormMetadataLoading,
		reactionTypeOptions,
		ruleFieldOptions,
		advancedTriggerOptions,
		collectionOptions,
	};
};

export default useRuleLifecycle;
